/**
 * Turning one stored phrase into the words on screen (SPEC §4, интерфейс).
 *
 * Deliberately tiny and deliberately pure: no store, no React, no `navigator`.
 * Everything that decides *which* language we are in lives in `locale.ts`; this
 * file only knows how to render a phrase once that decision is made, which is
 * what makes it testable without a DOM and reusable from the service worker.
 *
 * Two things it has to get right, and both are the reason we are not just
 * writing `dict[key]`:
 *
 *   1. **Plurals are not a Russian afterthought.** «1 сообщение / 2 сообщения /
 *      5 сообщений» is three forms where English has two, and the boundaries are
 *      not where a naive `count === 1` check puts them — 11 to 14 take the same
 *      form as 5, and 21 takes the same form as 1. `Intl.PluralRules` already
 *      knows every one of those rules for both languages, so we ask it rather
 *      than hand-rolling a table that will be wrong for the next language.
 *   2. **A missing value must be loud.** An unfilled `{name}` renders as
 *      `{name}`, not as nothing: a visible placeholder gets reported, an empty
 *      gap in a sentence does not.
 */

/** The categories `Intl.PluralRules` can hand back. */
export type PluralCategory = "zero" | "one" | "two" | "few" | "many" | "other";

/**
 * A phrase is one string, or a set of forms chosen by a count.
 *
 * `other` is required in the plural form because it is the one category every
 * language has — so there is always something to fall back to when a count
 * lands in a category this phrase never spelled out.
 */
export type Phrase =
  | string
  | ({ other: string } & Partial<Record<Exclude<PluralCategory, "other">, string>>);

/** Values substituted into `{placeholders}`. */
export type Vars = Record<string, string | number>;

/**
 * `Intl.PluralRules` instances are not cheap to build and we build one per
 * pluralised phrase per render otherwise.
 */
const pluralRules = new Map<string, Intl.PluralRules>();

function rulesFor(locale: string): Intl.PluralRules {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return rules;
}

/**
 * Fill `{placeholders}`.
 *
 * A name with no value is left exactly as written. That is the honest failure:
 * «Осталось {count}» on screen is a bug somebody reports, «Осталось » is a bug
 * nobody notices.
 */
export function interpolate(template: string, vars: Vars | undefined): string {
  if (!vars) return template;

  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    return value === undefined ? whole : String(value);
  });
}

/**
 * Pick the form and fill it in.
 *
 * `vars.count` does double duty — it selects the plural form *and* is available
 * to the template as `{count}`, so a phrase never has to be passed the same
 * number twice.
 */
export function translate(phrase: Phrase, locale: string, vars?: Vars): string {
  if (typeof phrase === "string") return interpolate(phrase, vars);

  const count = vars?.count;

  // A pluralised phrase asked for without a count cannot choose, and guessing
  // would silently pick the wrong form. `other` is the one form that always
  // exists, so it is what an author sees when they forget the count.
  if (typeof count !== "number") return interpolate(phrase.other, vars);

  const category = rulesFor(locale).select(count) as PluralCategory;
  const form = phrase[category as Exclude<PluralCategory, "other">] ?? phrase.other;

  return interpolate(form, vars);
}
